// @ts-check
/**
 * Zásuvka navigace pod 1024 px (R-20 sekce 2) — doplněk nativního popoveru, ne jeho náhrada.
 *
 * Kořen: řádek kostry `[data-module="drawer"]` (layout.gohtml) s `aside[data-drawer]`
 * (popover `#sidebar`, partial system/sidebar) a tlačítkem „Otevřít menu"
 * `[popovertarget="sidebar"]` bez `popovertargetaction` v liště. Bez JS dává otevření, Esc,
 * scrim i ✕ Popover API. S JS navíc (přechodová tabulka R-20,
 * docs/diagramy/state-zasuvka-navigace.puml):
 * - `toggle` → `open`: past fokusu `trapFocus` (fokus na první položku navigace `nav a[href]`,
 *   Tab / Shift+Tab cyklují v panelu i z fokusu mimo něj), `aria-expanded="true"` na tlačítku;
 * - `toggle` → `closed`: past zrušena, `aria-expanded="false"`, `returnFocus` na tlačítko
 *   (po kliku na scrim prohlížeč fokus nevrací);
 * - `matchMedia('(min-width: 64rem)')` → `change` na desktop při otevřené zásuvce:
 *   `hidePopover()` (od 1024 px je panel trvalý sloupec bez stavu).
 *
 * Stav „otevřená" drží modul z událostí `toggle`, ne z `:popover-open` (jsdom pseudotřídu nezná).
 * Žádné texty UI (popisky nese šablona), žádné `data-*` kromě `data-drawer`, nic mimo `root`;
 * bez `matchMedia` (jsdom) modul zavření při rozšíření okna vynechá a jinak běží.
 */

import { returnFocus, trapFocus } from './lib/focus.js';

const DESKTOP = '(min-width: 64rem)';

/**
 * Inicializace modulu nad kořenem `[data-module="drawer"]`.
 * @param {HTMLElement} root
 */
export default function init(root) {
  const aside = /** @type {HTMLElement | null} */ (root.querySelector('[data-drawer]'));
  const toggle = /** @type {HTMLElement | null} */ (
    root.querySelector('[popovertarget="sidebar"]:not([popovertargetaction])')
  );
  if (aside === null || toggle === null) return;
  const doc = root.ownerDocument;
  // AbortController z okna dokumentu: addEventListener přijme jen signál své sféry (jsdom).
  const win = doc.defaultView ?? globalThis;
  /** Past fokusu otevřené zásuvky; `null` = zavřená. @type {AbortController | null} */
  let trap = null;
  toggle.setAttribute('aria-expanded', 'false');

  aside.addEventListener('toggle', (event) => {
    const state = /** @type {{ newState?: string }} */ (event).newState;
    if (state === 'open') {
      trap?.abort();
      trap = new win.AbortController();
      const first = /** @type {HTMLElement | null} */ (aside.querySelector('nav a[href]'));
      trapFocus(aside, trap.signal, first);
      toggle.setAttribute('aria-expanded', 'true');
    } else if (state === 'closed') {
      trap?.abort();
      trap = null;
      toggle.setAttribute('aria-expanded', 'false');
      returnFocus(toggle, aside);
    }
  });

  if (typeof win.matchMedia !== 'function') return;
  win.matchMedia(DESKTOP).addEventListener('change', (event) => {
    if (event.matches && trap !== null && typeof aside.hidePopover === 'function') {
      aside.hidePopover();
    }
  });
}
